import {
  ASSESSMENT_LANGUAGES,
  DE,
  EN,
  FR,
  NL,
  getAssessmentLanguages
} from '../constants/languages'

// Sprache, auf die eine Mail zurückfällt, wenn System/MAIL_TEMPLATES die
// Sprache der Stelle noch nicht kennt.
export const MAIL_TEMPLATE_FALLBACK_LANGUAGE = EN

// Teile aus System/MAIL_TEMPLATES (siehe selectSystemData in helpers/index.js),
// die je Sprache hinterlegt sind.
const LOCALIZED_TEMPLATE_KEYS = [
  'defaultCareerMailTemplate',
  'defaultCareerMailTemplateReminder',
  'defaultCareerMailTemplateReject',
  'defaultPartnerMailTemplate',
  'defaultPartnerMailTemplateReminder',
  'defaultPartnerMailTemplateReject',
  'defaultCrossmodeMailTemplate'
]

// Gleiche Regel wie inviteTalent (mail-language.js), ein leerer Wert zählt als fehlend
const hasLanguage = (part, language) => Boolean(part[language])

/**
 * System/MAIL_TEMPLATES (generiert in aivy-assets/src/mail-templates.js) hat
 * bisher nur de und en. Eine Stelle kann aber auch auf nl oder fr stehen.
 * Fehlt die Sprache in irgendeinem Teil, fällt die ganze Mail auf en zurück.
 * So mischen Betreff, Text, Scaffold und Einladungsblock nie zwei Sprachen.
 * Teile, die im System ganz fehlen, zählen nicht, sonst würde eine fehlende
 * Vorlage auch de und en auf den Fallback zwingen.
 */
export const resolveMailTemplateLanguage = ({ system, language }) => {
  if (!language) return MAIL_TEMPLATE_FALLBACK_LANGUAGE

  const parts = [
    system?.scaffold,
    system?.invitationBlock,
    ...LOCALIZED_TEMPLATE_KEYS.flatMap((key) => [
      system?.[key]?.subject,
      system?.[key]?.data
    ])
  ].filter(Boolean)

  if (!parts.length) return language

  return parts.every((part) => hasLanguage(part, language))
    ? language
    : MAIL_TEMPLATE_FALLBACK_LANGUAGE
}

// de und en liegen seit jeher in MAIL_TEMPLATES und bleiben immer wählbar.
const BASE_ASSESSMENT_LANGUAGES = [DE, EN]

/**
 * Optionen für "Sprache des Assessments". nl und fr erscheinen erst, wenn
 * System/MAIL_TEMPLATES sie vollständig kennt. Vorher würde der Hinweis unter
 * der Auswahl Mails auf nl oder fr versprechen, verschickt würde aber en.
 * Außerdem bricht ein inviteTalent ohne Sprach-Fallback bei nl und fr ab.
 * Die gespeicherte Sprache der Stelle bleibt immer drin, damit die Auswahl
 * nie leer ist.
 */
export const getSelectableAssessmentLanguages = ({ system, currentLanguage }) =>
  getAssessmentLanguages().filter(
    ([language]) =>
      language === currentLanguage ||
      BASE_ASSESSMENT_LANGUAGES.includes(language) ||
      resolveMailTemplateLanguage({ system, language }) === language
  )

// Sprachen, die ein Paket sperren kann, mit ihrem Key aus FEATURES
// (features/pricing/plans.js). de und en sind in jedem Paket enthalten.
// SelectMenu holt sich hierüber den Paketnamen für die Pille.
export const ASSESSMENT_LANGUAGE_FEATURES = Object.freeze({
  [NL]: 'LANGUAGE_NL',
  [FR]: 'LANGUAGE_FR'
})

/**
 * Gesperrte Optionen für "Sprache des Assessments", als lockedOptions für
 * SelectMenu. fr und nl sind die locked-Werte von useFeatureGate('LANGUAGE_FR')
 * und useFeatureGate('LANGUAGE_NL'), also nur in enforced true.
 *
 * currentLanguage ist die gespeicherte Sprache der Stelle. Sie bleibt
 * wählbar, eine bestehende fr- oder nl-Stelle behält ihre Sprache. Weg
 * wechseln geht, nach dem Speichern zurück nicht mehr.
 * options sind die Optionen aus getSelectableAssessmentLanguages. Gesperrt
 * wird nur, was dort überhaupt angeboten wird. Ohne options gilt jede
 * Sprache, SelectMenu ignoriert Keys, die nicht in seinen Optionen liegen.
 *
 * @param {{ fr?: boolean, nl?: boolean, currentLanguage?: string,
 *   options?: Array<[string, string]> }} [params]
 * @returns {string[]} gesperrte Sprach-Keys in der Reihenfolge von
 *   ASSESSMENT_LANGUAGES
 */
export const getLockedAssessmentLanguages = ({
  fr = false,
  nl = false,
  currentLanguage,
  options
} = {}) => {
  const locked = { [FR]: !!fr, [NL]: !!nl }
  const offered = options ? options.map(([language]) => language) : null

  return ASSESSMENT_LANGUAGES.filter(
    (language) =>
      locked[language] &&
      language !== currentLanguage &&
      (!offered || offered.includes(language))
  )
}
